import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTable from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { statutoryActions } from '@nxsys/store/slices/statutory/reducer/statutorySlice';
import { GetStatutoryLeaveListParams } from '@nxsys/store/slices/statutory/types';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { Dropdown } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../../components/ActionsHeader';
import { getColumns } from './columns';
import { ExportToCsv } from 'export-to-csv';
import moment from 'moment';
import AddNewTimeSheet from '../../components/AddNewTimeSheet';
import ProcessStatutoryModal from '../../components/ProcessStatutoryModal';
import { STATUTORY_TYPE } from '@nxsys/constants/statutory';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { showToast } from '@nxsys/utils/toast';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

const StatutoryPayment = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const taxYear = getTaxWeekTaxYear().taxYear;
  const { isBureau, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { statutoryPaymentData, loading } = useSelector(
    (state: AppState) => state.statutoryStore,
  );
  const { list, pagination, queryParams } = statutoryPaymentData;
  const [addNewTimeSheetModal, setIsAddNewTimeSheetModal] = useState<{
    open: boolean;
    data: any;
  }>({
    open: false,
    data: null,
  });
  const [processStatutoryModal, setProcessStatutoryModal] = useState<{
    open: boolean;
    processType: 'selected' | 'all';
  }>({
    open: false,
    processType: 'all',
  });
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const navigate = useNavigateWithQuery();

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['statutory.payment.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: '1',
          label: messages['statutory.payment.exportSelected'],
          onClick: handleExportSelected,
        },
        {
          key: '2',
          label: messages['statutory.payment.exportAll'],
          onClick: handleExportAll,
        },
        {
          key: '3',
          label: messages['statutory.payment.processSelected'],
          onClick: () => handleClickProcessStatutory('selected'),
        },
        {
          key: '4',
          label: messages['statutory.payment.processAll'],
          onClick: () => handleClickProcessStatutory('all'),
        },
      ],
    };

    return (
      <div className="flex items-center gap-2">
        <Dropdown menu={menuProps as any}>
          <AppButton
            type="primary"
            name={messages['common.bulkAction']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear,
    });
  }, []);

  const handleFetchData = (params: GetStatutoryLeaveListParams) => {
    const newParams = { ...params };

    newParams.companyIds = resolveFilterCompanyIds(
      newParams.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(statutoryActions.getStatutoryPaymentRecords(newParams));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleExport = (data: any) => {
    const dataExport = data.map((item: any) => {
      const {
        type,
        amount,
        // status,
        taxWeek,
        taxYear,
        forename,
        surname,
        agencyPrefix,
        agencyRef,
        primaryAgencyName,
        companyName,
      } = item;
      return {
        type: type || '',
        amount: amount || '',
        // status: status || '',
        taxWeek: taxWeek || '',
        taxYear: taxYear || '',
        id: item.id || '',
        forename: forename || '',
        surname: surname || '',
        agencyRef:
          `${agencyPrefix || '___'}/${agencyRef || item.id || '_'}` || '',
        primaryAgencyName: primaryAgencyName || '',
        companyName: companyName || '',
      };
    });

    const headers = [
      'Type',
      'Amount',
      // 'Status',
      'Tax period',
      'Tax year',
      'Employee ID',
      'forename',
      'surname',
      'Agency Ref',
      'Primary Agency',
      'Payment Company',
    ];

    const csvExporter = new ExportToCsv({
      filename:
        `Pension-contributions-export-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}` || 'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showToast({ type: 'warning', message: messages['common.noDataToExport'] });
      return;
    }
    const res = await dispatch(
      statutoryActions.getStatutoryPaymentRecords({
        ...queryParams,
        page: 0,
        size: pagination.totalDocs,
      }),
    ).unwrap();
    handleExport(res?.data);
  };

  const handleExportSelected = async () => {
    if (selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }

    handleExport(selectedRows);
  };

  const toggleAddNewTimeSheetModal = () => {
    setIsAddNewTimeSheetModal({
      ...addNewTimeSheetModal,
      open: !addNewTimeSheetModal.open,
    });
  };

  const handleAddTimeSheet = async (data: any) => {
    if (isBureau) {
      await dispatch(
        statutoryActions.createTimeSheetStatutory({
          records: [{ id: data?.id, type: data?.type }],
        }),
      );
      refetchData();
      return;
    }
    setIsAddNewTimeSheetModal({
      open: true,
      data,
    });
  };

  const handleSaveTimeSheet = async (values: any) => {
    await dispatch(
      statutoryActions.createTimeSheetStatutory({
        records: [
          {
            agencyRefId: values?.agencyName?.key,
            type: addNewTimeSheetModal?.data?.type,
            id: addNewTimeSheetModal?.data?.id,
          },
        ],
      }),
    );
    toggleAddNewTimeSheetModal();
    refetchData();
  };

  const toggleProcessStatutoryModal = () => {
    setProcessStatutoryModal({
      ...processStatutoryModal,
      open: !processStatutoryModal.open,
    });
  };

  const handleClickProcessStatutory = async (type: 'selected' | 'all') => {
    if (selectedRows.length === 0 && type === 'selected') {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    if (isBureau) {
      let records: { id: number; type: string }[];
      if (type === 'selected') {
        records = selectedRows.map((item: any) => ({
          id: item?.id,
          type: item?.type,
        }));
      } else {
        const res = await dispatch(
          statutoryActions.getStatutoryPaymentRecords({
            ...queryParams,
            page: 0,
            size: pagination.totalDocs,
          }),
        ).unwrap();
        records = (res?.data || []).map((item: any) => ({
          id: item?.id,
          type: item?.type,
        }));
      }
      await dispatch(statutoryActions.createTimeSheetStatutory({ records }));
      refetchData();
      return;
    }
    setProcessStatutoryModal({
      open: true,
      processType: type,
    });
  };

  const handleSaveProcess = async (values: any) => {
    let records = [];
    if (processStatutoryModal.processType === 'selected') {
      records = selectedRows.map((item: any) => {
        return {
          agencyRefId: values?.agencyName?.key,
          type: item?.type,
          id: item?.id,
        };
      });
    } else {
      const res = await dispatch(
        statutoryActions.getStatutoryPaymentRecords({
          ...queryParams,
          page: 0,
          size: pagination.totalDocs,
        }),
      ).unwrap();
      const data = res?.data;
      records = data.map((item: any) => {
        return {
          agencyRefId: values?.agencyName?.key,
          type: item?.type,
          id: item?.id,
        };
      });
    }
    await dispatch(
      statutoryActions.createTimeSheetStatutory({
        records,
      }),
    );
    toggleProcessStatutoryModal();
    refetchData();
  };

  const handleViewDetail = (type: string, id: string) => {
    navigate(`/statutory-payment/${type}/${id}`);
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.taxYear ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds ||
    !!queryParams?.type ||
    !!queryParams?.payFrequency;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <ActionsHeader
            onFilter={handleFetchData}
            type={STATUTORY_TYPE.PAYMENT}
            fetchData={handleFetchData}
          />
        </AppsHeader>
        <AppsContent>
          <AppTable
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2000, y: 700 }}
            titleEmpty={messages['statutory.payment.empty']}
            columns={getColumns({
              handleAddTimeSheet,
              messages,
              handleViewDetail,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <AddNewTimeSheet
        isOpen={addNewTimeSheetModal.open}
        toggle={toggleAddNewTimeSheetModal}
        data={addNewTimeSheetModal.data}
        onSave={handleSaveTimeSheet}
      />
      <ProcessStatutoryModal
        isOpen={processStatutoryModal.open}
        toggle={toggleProcessStatutoryModal}
        onSave={handleSaveProcess}
      />
    </AppsContainer>
  );
};

export default StatutoryPayment;
